import _isNil from 'lodash/isNil';

import {
  getGoogleLocationProviderSettings,
  getGoogleProfileProviderSetting,
} from 'components/modules/business-profile/services/provider-service';
import { multiCaseGet, objectToSnakeCase } from 'libs/format';

import { GMB_AUTH_ERROR_ACCESS_DENIED } from './ProfilePanel.constants';

export function hasGoogleMyBusinessToken(providerServiceSettings) {
  return providerServiceSettings.some(
    (serviceSettings) =>
      multiCaseGet(serviceSettings, 'provider') === 'Google' &&
      multiCaseGet(serviceSettings, 'serviceType') === 'Profile' &&
      multiCaseGet(serviceSettings, 'serviceData.tokenPresent'),
  );
}

export function getErrorHeaderAndMessage(errorCode, brandName) {
  let header = 'Google Business Profile authorization failed';
  let error =
    `There was an error when trying to authorize ${brandName} access to your ` +
    'Google Business Profile account. Please, try again. If the problem persist ' +
    'contact our support team.';

  if (errorCode === GMB_AUTH_ERROR_ACCESS_DENIED) {
    header = 'Google Business Profile access permission was denied';
    error =
      `You need to give ${brandName} permission to ` +
      'synchronize information from your business like hours, location and ' +
      'menu with Google Business Profile';
  }

  return [header, error];
}

export function getGoogleMyBusinessAccountId(providerServiceSettings) {
  const googleProfileSettings = providerServiceSettings.find(
    (serviceSettings) =>
      multiCaseGet(serviceSettings, 'provider') === 'Google' &&
      multiCaseGet(serviceSettings, 'serviceType') === 'Profile' &&
      multiCaseGet(serviceSettings, 'serviceData.gmbAccountId'),
  );
  return multiCaseGet(googleProfileSettings, 'serviceData.gmbAccountId');
}

export function dedupeGoogleLocations(locations) {
  if (!Array.isArray(locations)) {
    return locations;
  }

  const seenLocationIds = new Set();
  return locations.filter((location) => {
    const locationId = multiCaseGet(location, 'locationId');
    if (!locationId || seenLocationIds.has(locationId)) {
      return false;
    }
    seenLocationIds.add(locationId);
    return true;
  });
}

export function updateServiceSetting(serviceSetting, data) {
  const { serviceData = {} } = serviceSetting;
  return {
    ...serviceSetting,
    serviceData: {
      ...serviceData,
      ...data,
    },
  };
}

export function getAccountSelectPayload(serviceSetting, data) {
  const payload = updateServiceSetting(serviceSetting, data);

  return objectToSnakeCase(payload);
}

export function getLocationSettingsChangePayload(serviceSetting, newSyncSettings) {
  const { serviceData = {} } = serviceSetting;

  const payload = updateServiceSetting(serviceSetting, {
    ...serviceData,
    settings: newSyncSettings,
  });

  return objectToSnakeCase(payload);
}

export function getAssociatedLocations(locations, locationServiceSettings) {
  if (locations) {
    const gmbLocationIds = new Set(
      locationServiceSettings
        .filter((serviceSettings) => !!multiCaseGet(serviceSettings, 'serviceData.gmbLocationId'))
        .map((serviceSettings) => multiCaseGet(serviceSettings, 'serviceData.gmbLocationId')),
    );
    return locations.filter((location) => gmbLocationIds.has(multiCaseGet(location, 'locationId')));
  }
  return null;
}

export function hasGoogleProfileServiceSetting(providerServiceSettings = []) {
  const profile = getGoogleProfileProviderSetting(providerServiceSettings);
  const gmbAccountId = multiCaseGet(profile, 'serviceData.gmbAccountId');
  return !_isNil(gmbAccountId);
}

export function hasGoogleMyBusinessLocation(providerServiceSettings = []) {
  const locationSettings = getGoogleLocationProviderSettings(providerServiceSettings);
  return locationSettings.some(
    (locationServiceSettings) =>
      !_isNil(multiCaseGet(locationServiceSettings, 'serviceData.gmbLocationId')),
  );
}

export function getCurrentStep(providerServiceSettings) {
  if (!hasGoogleMyBusinessToken(providerServiceSettings)) {
    return 1;
  }
  if (!hasGoogleProfileServiceSetting(providerServiceSettings)) {
    return 2;
  }
  if (!hasGoogleMyBusinessLocation(providerServiceSettings)) {
    return 3;
  }
  return 4;
}

export function getConnectionSteps(currentStep, brandName) {
  if (currentStep > 3) {
    return [
      {
        id: 'connected',
        title: 'Connected',
        description: 'You are connected to Google Business Profile',
        icon: 'cogs',
        completed: true,
      },
    ];
  }

  return [
    {
      id: 'create-connection',
      title: 'Connect',
      description: `Authorize ${brandName} to manage your Google Business Profile`,
      icon: 'plug',
      active: currentStep === 1 || currentStep === 2,
      completed: currentStep > 2,
    },
    {
      id: 'confirm-connection',
      title: 'Confirm',
      description: 'Confirm we have everything correct',
      icon: 'cogs',
      active: currentStep === 3,
      completed: currentStep > 3,
    },
  ];
}
